import { createContext, useEffect, useState, useCallback } from "react";
import { useAuthContext } from "../hooks/useAuthContext";
import getAuthHeaders from "../utils/getAuthHeaders";

export const FavoritesContext = createContext();

const API_BASE_URL =
  process.env.NODE_ENV === "development"
    ? `${process.env.REACT_APP_NETWORK}/api/favorites`
    : "/api/favorites";

export function FavoritesProvider({ children }) {
  const { user } = useAuthContext();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch favorites from server
  const fetchFavorites = useCallback(async () => {
    if (!user) {
      setFavorites([]);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const authHeader = await getAuthHeaders(user);

      const response = await fetch(API_BASE_URL, {
        headers: {
          "Content-Type": "application/json",
          ...authHeader,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch favorites: ${response.statusText}`);
      }

      const data = await response.json();
      setFavorites(data.favorites || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching favorites:", err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch favorites when user logs in
  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Add product to favorites
  const addFavorite = useCallback(
    async (productId) => {
      if (!user) return;

      // Optimistically update UI
      setFavorites((prev) => {
        if (prev.includes(productId)) return prev;
        return [...prev, productId];
      });

      try {
        const authHeader = await getAuthHeaders(user);

        const response = await fetch(`${API_BASE_URL}/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({ productId }),
        });

        if (!response.ok) {
          throw new Error(`Failed to add favorite: ${response.statusText}`);
        }

        const data = await response.json();
        setFavorites(data.favorites || []);
        setError(null);
      } catch (err) {
        console.error("Error adding favorite:", err);
        setError(err.message);
        // Revert optimistic update on error
        await fetchFavorites();
      }
    },
    [user, fetchFavorites]
  );

  // Remove product from favorites
  const removeFavorite = useCallback(
    async (productId) => {
      if (!user) return;

      // Optimistically update UI
      setFavorites((prev) => prev.filter((id) => id !== productId));

      try {
        const authHeader = await getAuthHeaders(user);

        const response = await fetch(`${API_BASE_URL}/${productId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to remove favorite: ${response.statusText}`);
        }

        const data = await response.json();
        setFavorites(data.favorites || []);
        setError(null);
      } catch (err) {
        console.error("Error removing favorite:", err);
        setError(err.message);
        // Revert optimistic update on error
        await fetchFavorites();
      }
    },
    [user, fetchFavorites]
  );

  // Toggle favorite status
  const toggleFavorite = useCallback(
    async (productId) => {
      if (favorites.includes(productId)) {
        await removeFavorite(productId);
      } else {
        await addFavorite(productId);
      }
    },
    [favorites, addFavorite, removeFavorite]
  );

  // Check if product is favorited
  const isFavorite = useCallback(
    (productId) => {
      return favorites.includes(productId);
    },
    [favorites]
  );

  const value = {
    favorites,
    isLoading,
    error,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    refetchFavorites: fetchFavorites,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}
